import React, { useCallback, useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_POCKET_API_URL || "https://global.iyztp.com/api/pocket/v1";
const STORAGE = "tomi-code-orders-v1";
const validOrder = (order) => order && /^pc_[a-f0-9]{32}$/.test(order.orderId) && /^[a-f0-9]{64}$/.test(order.token) && ["native", "h5"].includes(order.mode);
function readOrders() {
  try { return JSON.parse(localStorage.getItem(STORAGE) || "[]").filter(validOrder); }
  catch (_) { return []; }
}
function saveOrders(orders) {
  localStorage.setItem(STORAGE, JSON.stringify(orders));
  if (localStorage.getItem(STORAGE) !== JSON.stringify(orders)) throw new Error("请允许浏览器保存订单后再购买。");
}
function randomHex(bytes) {
  const data = new Uint8Array(bytes);
  window.crypto.getRandomValues(data);
  return Array.from(data, (v) => v.toString(16).padStart(2, "0")).join("");
}
function safeHttps(value) {
  try { const url = new URL(value); return url.protocol === "https:" && !url.username && !url.password; }
  catch (_) { return false; }
}
async function api(path, body, signal) {
  const timeout = new AbortController();
  const abort = () => timeout.abort();
  if (signal) signal.addEventListener("abort", abort);
  const timer = setTimeout(abort, 15000);
  try {
    const response = await fetch(`${API}/codes/${path}`, {
      method: body ? "POST" : "GET", cache: "no-store", signal: timeout.signal,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const failure = await response.json().catch(() => null);
      // Spring's unmapped-route 404 is a deployment failure, not a lost order.
      const missingOrder = response.status === 404 && failure?.error === "购买操作未完成";
      throw new Error(response.status === 429 ? "操作频繁，请稍后重试。" : missingOrder ? "订单尚未创建或凭证不正确，可重试下单。" : "购买服务暂不可用，请稍后重试。");
    }
    return await response.json();
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", abort);
  }
}
export default function TomiPurchase() {
  const [orders, setOrders] = useState(readOrders);
  const [selected, setSelected] = useState(() => readOrders()[0] || null);
  const [receipt, setReceipt] = useState(null);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const active = useRef(null);
  const checkoutRequest = useRef(null);
  const action = useRef(false);
  const current = useRef(selected);
  current.current = selected;
  const choose = useCallback((order) => {
    active.current?.abort();
    current.current = order;
    setSelected(order); setReceipt(null); setError(""); setNotice("");
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    api("product", null, controller.signal).then((value) => {
      if (!controller.signal.aborted && /^\d+\.\d{2}$/.test(value.amount)) setProduct(value);
    }).catch(() => {});
    const match = window.location.hash.match(/^#receipt=(pc_[a-f0-9]{32})\.([a-f0-9]{64})$/);
    if (match) {
      const old = readOrders();
      const order = old.find((o) => o.orderId === match[1]) || { orderId: match[1], token: match[2], mode: "h5" };
      order.token = match[2];
      const next = [order, ...old.filter((o) => o.orderId !== order.orderId)];
      try {
        saveOrders(next); setOrders(next); choose(order);
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      } catch (_) { choose(order); setError("浏览器未能保存订单，请先复制订单凭证链接。"); }
    }
    return () => { controller.abort(); active.current?.abort(); checkoutRequest.current?.abort(); };
  }, [choose]);
  const refresh = useCallback(async (order) => {
    if (!order || action.current) return;
    active.current?.abort();
    const controller = new AbortController(); active.current = controller;
    try {
      const value = await api("receipt", { orderId: order.orderId, token: order.token }, controller.signal);
      if (!controller.signal.aborted && current.current?.orderId === order.orderId) { setReceipt(value); setError(""); }
    } catch (failure) {
      if (!controller.signal.aborted && current.current?.orderId === order.orderId) setError(failure.message);
    }
  }, []);
  useEffect(() => {
    if (!selected) return;
    refresh(selected);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refresh(selected);
    }, 5000);
    const focus = () => refresh(selected);
    window.addEventListener("focus", focus);
    return () => { clearInterval(timer); window.removeEventListener("focus", focus); active.current?.abort(); };
  }, [selected, refresh]);
  async function checkout(newOrder = false) {
    if (action.current) return;
    if (newOrder && selected && receipt?.state !== "paid" && !window.confirm("当前订单尚未确认付款。确定另外购买一个激活码？已付款但未显示结果时，请先刷新订单。")) return;
    action.current = true; setBusy(true); setError(""); active.current?.abort();
    const controller = new AbortController(); checkoutRequest.current = controller;
    try {
      let order = !newOrder ? selected : null;
      if (!order) {
        order = { orderId: `pc_${randomHex(16)}`, token: randomHex(32), mode: /Android|iPhone|iPad|iPod|HarmonyOS/i.test(navigator.userAgent) ? "h5" : "native" };
        const next = [order, ...readOrders()];
        // Persist the only receipt credential before any payment can be created.
        saveOrders(next); setOrders(next); current.current = order; setSelected(order); setReceipt(null);
      }
      const value = await api("checkout", order, controller.signal);
      if (!controller.signal.aborted && current.current?.orderId === order.orderId) setReceipt(value);
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure.name === "AbortError" ? "请求超时，请重试原订单。" : failure.message);
    } finally { action.current = false; setBusy(false); }
  }
  async function copy(text, success) {
    try { await navigator.clipboard.writeText(text); setNotice(success); }
    catch (_) { setNotice("自动复制失败，请长按或选中文字手动复制。"); }
  }
  const receiptLink = selected ? `${window.location.origin}/tomi#receipt=${selected.orderId}.${selected.token}` : "";
  return <section className="tomi-purchase" aria-label="购买激活码">
    <div className="tomi-purchase-heading"><h2>永久解锁激活码</h2><strong>{product ? `¥${product.amount}` : "以订单金额为准"}</strong></div>
    {!selected && <button className="tomi-button tomi-primary" disabled={busy} onClick={() => checkout()}>{busy ? "正在创建订单…" : "微信购买激活码"}</button>}
    {selected && <div className="tomi-order">
      <p className="tomi-order-id">订单：{selected.orderId}</p>
      {receipt?.state === "paid" ? <>
        <h3>支付成功</h3>
        <label htmlFor="tomi-activation-code">你的激活码</label>
        <input id="tomi-activation-code" readOnly value={receipt.activationCode || ""} onFocus={(event) => event.target.select()} />
        <button className="tomi-button tomi-primary" onClick={() => copy(receipt.activationCode, "激活码已复制。")}>复制激活码</button>
        <p>{receipt.activated ? "已绑定设备，转移前请在原设备反激活。" : "打开拓迷的购买页，输入激活码即可激活。"}</p>
      </> : <>
        {receipt && <p>待支付 <strong>¥{receipt.amount}</strong></p>}
        {safeHttps(receipt?.paymentUrl) && (receipt.mode === "native" ? <>
          <img className="tomi-pay-qr" src={receipt.paymentUrl} referrerPolicy="no-referrer" alt="使用微信扫描此付款二维码" />
          <p>请使用微信扫一扫付款</p>
        </> : <a className="tomi-button tomi-primary" href={receipt.paymentUrl} referrerPolicy="strict-origin">打开微信支付</a>)}
        {!receipt?.paymentUrl && <button className="tomi-button tomi-primary" disabled={busy} onClick={() => checkout()}>{busy ? "正在创建订单…" : "继续创建付款订单"}</button>}
        <p className="tomi-purchase-note">付款后返回此页领取激活码，结果确认可能稍有延迟。</p>
      </>}
      <button className="tomi-text-button" disabled={busy} onClick={() => refresh(selected)}>刷新付款结果</button>
      <details><summary>保存订单凭证</summary>
        <p className="tomi-purchase-note">此链接可找回激活码，请私下保存。清除浏览器数据后可用它恢复订单。</p>
        <input aria-label="订单凭证链接" readOnly value={receiptLink} onFocus={(event) => event.target.select()} />
        <button className="tomi-text-button" onClick={() => copy(receiptLink, "订单凭证链接已复制，请妥善保存。")}>复制订单凭证链接</button>
      </details>
      <button className="tomi-text-button" disabled={busy} onClick={() => checkout(true)}>再购买一个激活码</button>
    </div>}
    {orders.length > 1 && <label className="tomi-history">本浏览器的订单
      <select aria-label="历史订单" value={selected?.orderId || ""} disabled={busy} onChange={(event) => choose(orders.find((order) => order.orderId === event.target.value))}>
        {orders.map((order) => <option value={order.orderId} key={order.orderId}>{order.orderId}</option>)}
      </select>
    </label>}
    {error && <p className="tomi-error" role="alert">{error}</p>}
    {notice && <p aria-live="polite">{notice}</p>}
  </section>;
}
